<script lang="ts" setup>
import * as Sentry from "@sentry/vue";
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { useApp, useAuth, usePanicButton } from "../../../../store";
import { showDialog } from "../../../../utils/dialog";
import SidebarMenu, { MenuItems } from "../../SidebarMenu";

const authStore = useAuth();
const { user } = storeToRefs(authStore);
const appStore = useApp();
const panicButtonStore = usePanicButton();
const title = "User Settings";
const sidebarMenuRef = ref<typeof SidebarMenu | null>(null);
const items = computed(() => {
  const newItems: MenuItems = [
    {
      label: "Change Workspace",
      permission: true,
      to: {
        name: "tenants",
      },
    },
    {
      label: "My Account",
      permission: true,
      to: "/staff/my-account",
    },
    {
      label: "Keyboard Shortcuts",
      permission: true,
      onClick: () => {
        appStore.setKeyboardShortcutsModalVisible(true);
        sidebarMenuRef.value?.close();
      },
    },
    {
      label: "Send User Session",
      permission: true,
      onClick: async () => {
        sidebarMenuRef.value?.close();
        // ENG-20571 start replay if session wasn't sampled, then flush
        const replay = Sentry.getReplay();
        if (replay && !replay.getReplayId()) replay.start();
        await replay?.flush();
        Sentry.captureFeedback({
          message: [
            "User-initiated session report from the user menu",
            `URL: ${window.location.href}`,
            `UA: ${navigator.userAgent}`,
            `@ ${new Date().toISOString()}`,
          ].join("\n"),
          name: user.value?.username,
          email: user.value?.username,
        });
        showDialog({
          title: "Session Sent",
          text:
            "Thank you.\n\n" +
            "Our team will now receive your session information via our monitoring tools.\n\n" +
            "Please note: This option is not monitored as a support inbox and should only be used if our team have asked you to use it.",
          noCancel: true,
          okLabel: "Dismiss",
          okColor: "submit",
        });
      },
    },
    {
      label: "Logout",
      permission: true,
      to: {
        name: "logout",
      },
    },
  ];

  if (panicButtonStore.allAlertsCount > 0) {
    newItems.unshift({
      label: `Active Panic Alerts (${panicButtonStore.allAlertsCount})`,
      permission: true,
      onClick: () => {
        panicButtonStore.showPanicAlertModal();
        sidebarMenuRef.value?.close();
      },
    });
  }

  return newItems;
});
const additionalInfo = computed(() => {
  let data = [];
  if (authStore.currentTenant?.name) {
    data.push({ label: "Organisation", value: authStore.currentTenant.name });
  }

  if (authStore.currentLocationValue?.label) {
    data.push({ label: "Location", value: authStore.currentLocationValue.label });
  }
  return data;
});
</script>
<template>
  <SidebarMenu
    ref="sidebarMenuRef"
    :title="title"
    :items="items"
    :additional-info="additionalInfo"
    role="navigation"
    aria-label="User menu"
  />
</template>
